import type { DragEndEvent } from '@dnd-kit/core';
import type {
  AccessLevelProp,
  SizerProp,
  TableHeadCellTooltipProp,
  TableVariantProp,
} from '@meterup/atto';
import type {
  Cell,
  Column,
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  FilterFnOption,
  FilterMeta,
  OnChangeFn,
  Row,
  RowData,
  RowSelectionState,
  SortingFn,
  SortingState,
  Table as ReactTable,
  TableState,
  VisibilityState,
} from '@tanstack/react-table';
import type { Dispatch, ForwardedRef, JSX, SetStateAction } from 'react';
import type { To } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CheckboxInput,
  selectors,
  ShadowOnScrollOverflowContents,
  sizer,
  styled,
  Table,
  TableBody,
  TableCell,
  TableCellBuffer,
  TableCellState,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  UtilityControl,
} from '@meterup/atto';
import { rankings, rankItem } from '@tanstack/match-sorter-utils';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import React, {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { safeParseAddress4 } from '../topics/ipv4';
import { EnhancedReactRouterLink } from './ReactRouterLink';

export type { ColumnDef, Row, SortingState };
export { createColumnHelper, rankings, rankItem };

export type AutoTableAlignmentProp = 'start' | 'center' | 'end';
export type AutoTableStuckProp = 'top' | 'right' | 'bottom' | 'left';
export type AutoTableOverflowProp = 'auto' | 'hidden' | 'scroll' | 'visible';

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-shadow
  interface ColumnMeta<TData extends RowData, TValue> {
    isLeading?: boolean;
    alignment?: AutoTableAlignmentProp;
    group?: string | null;
    hasField?: boolean;
    hideSortIcon?: boolean;
    minBreakpoint?: 'sm' | 'md' | 'lg' | 'xl';
    tooltip?: TableHeadCellTooltipProp;
    stuck?: AutoTableStuckProp;
    accessLevel?: AccessLevelProp | undefined | null;
    width?: SizerProp;
    maxWidth?: SizerProp;
    minWidth?: SizerProp;
    overflow?: AutoTableOverflowProp;
    hideIfAllNullish?: boolean;
    hideIfAllEqual?: boolean;
  }

  interface FilterFns {
    equalsIfSet: FilterFn<any>;
    arrIncludesIfSet: FilterFn<any>;
    prefixOverlapsIfSet: FilterFn<any>;
  }
}

const macRegexp = /([\da-fA-F]{2}[-.:_]){5}[\da-fA-F]{2}/gi;

function normalizeMACs(input: string): string {
  const matches = input.matchAll(macRegexp);
  if (matches) {
    let s = input;
    for (const match of matches) {
      s = s.replace(match[0], match[0].replaceAll(/[-.:_]/g, ':'));
    }
    return s;
  }

  return input;
}

// Based on https://tanstack.com/table/v8/docs/api/features/filters#filter-meta
function defaultGlobalFilterFn<D extends any>(
  row: Row<D>,
  columnId: string,
  filterValue: string,
  addMeta: (meta: FilterMeta) => void,
) {
  const pattern = normalizeMACs(filterValue);

  const itemRank = rankItem(row.getValue(columnId), pattern);

  addMeta({ itemRank });

  return itemRank.passed;
}

const getSizeProps = (_: ReactTable<any>, column: Column<any>) => {
  const meta = column.columnDef.meta ?? {};
  const {
    alignment = 'start',
    group,
    hasField = false,
    hideSortIcon,
    width,
    maxWidth,
    minWidth,
    overflow,
  } = meta;

  return {
    alignment,
    group,
    hasField,
    hideSortIcon,
    style: {
      // eslint-disable-next-line no-nested-ternary
      width: maxWidth ? sizer(maxWidth) : width ? sizer(width) : undefined,
      // eslint-disable-next-line no-nested-ternary
      minWidth: minWidth ? sizer(minWidth) : width ? sizer(width) : undefined,
      overflow,
    },
  };
};

const getRowRange = <T extends any>(
  rows: Row<T>[],
  startIndex: number,
  endIndex: number,
): Row<T>[] => {
  const rangeStart = Math.min(startIndex, endIndex);
  const rangeEnd = Math.max(startIndex, endIndex);
  return rows.filter((row) => {
    const rowIndex = rows.indexOf(row);
    return rowIndex >= rangeStart && rowIndex <= rangeEnd;
  });
};

const SELECT_COLUMN_ID = 'select-col';

export const autoTableSelectorWidth = {
  '@notDesktop': '$46',
  '@desktop': '$38',
};

const AutoTableHeadCellSelector = styled(TableHeadCell, {
  '@notDesktop': {
    width: autoTableSelectorWidth['@notDesktop'],
  },

  '@desktop': {
    width: autoTableSelectorWidth['@desktop'],
  },
});

const AutoTableCellSelector = styled(TableCell, {
  '@notDesktop': {
    width: autoTableSelectorWidth['@notDesktop'],
  },

  '@desktop': {
    width: autoTableSelectorWidth['@desktop'],
  },
});

const AutoTableRowSelectorContainer = styled('label', {
  display: 'flex',
  cursor: 'pointer',
  width: '$14',
});

function AutoTableRowSelector<D extends any>({
  row,
  table,
  isRowSelectable = true,
}: {
  row: Row<D>;
  table: ReactTable<D>;
  isRowSelectable?: boolean;
}) {
  const lastSelectedIDRef = useRef<any>(null);
  return (
    <AutoTableRowSelectorContainer
      tabIndex={-1}
      onClick={(e) => {
        // prevent click from propagating to row
        if (e.currentTarget.tagName === 'INPUT') return;
        e.stopPropagation();
      }}
    >
      <CheckboxInput
        aria-label={row.getIsSelected() ? 'Deselect row' : 'Select row'}
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect() || !row.getCanMultiSelect() || !isRowSelectable}
        onClick={(e: React.MouseEvent<HTMLInputElement>): void => {
          e.preventDefault();
          e.stopPropagation();

          if (e.shiftKey) {
            const { rows } = table.getSortedRowModel();

            const startIndex = rows.findIndex((r) => r.index === row.index);
            const endIndex = rows.findIndex((r) => r.index === Number(lastSelectedIDRef.current));

            const rowsToToggle = getRowRange(rows, startIndex, endIndex);
            const isCellSelected = rows.find((r) => r.index === row.index)?.getIsSelected();

            table.setRowSelection((old) => ({
              ...old,
              ...Object.fromEntries(rowsToToggle.map((r) => [r.id, !isCellSelected])),
            }));
          } else {
            row.toggleSelected();
          }

          lastSelectedIDRef.current = row.index;
        }}
      />
    </AutoTableRowSelectorContainer>
  );
}

export interface IDColumnFilter<K extends string, V = unknown> {
  id: K;
  value: V;
}
export interface IDColumnSort<K extends string> {
  id: K;
  desc: boolean;
}
export type IDColumnVisibility<K extends string> = Partial<Record<K, boolean>>;

type GetLinkTo<D extends any> = (row: D) => To | null | undefined;
type IsRowDisabled<D> = (row: D) => boolean;
type IsRowSelected<D> = (row: D) => boolean;
type IsRowCollapsed<D> = (row: D) => boolean;
type OnRowDeselect<D> = (row: D) => void;
type OnRowClick<D> = (row: D) => void;
type OnRowReorder = (fromIndex: number, toIndex: number) => void;

type SubAutoTableProps<D> = {
  data: D;
  isNested: boolean;
};

function AutoTableCellImpl<D extends any, E extends React.ElementType>({
  as,
  cell,
  table,
  isNavigable,
  isNested,
  to,
  hotkeyTos,
  applyLinkToAllCells,
}: {
  as?: E;
  cell: Cell<D, any>;
  table: ReactTable<D>;
  isMultiSelected?: boolean;
  isNavigable?: boolean;
  isNested?: boolean;
  to?: To;
  hotkeyTos?: Record<string, To>;
  applyLinkToAllCells?: boolean;
}) {
  if (cell.column.columnDef.meta?.accessLevel === null) return null;

  const content = flexRender(cell.column.columnDef.cell, cell.getContext());
  const { hideSortIcon, ...sizeProps } = getSizeProps(table, cell.column);

  const meta = cell.column.columnDef.meta ?? {};
  const shouldUseLink = meta.isLeading || applyLinkToAllCells;
  const isNonLeadingWithLink = !meta.isLeading && applyLinkToAllCells;

  return (
    <TableCell
      key={cell.id}
      {...sizeProps}
      isLeading={meta.isLeading}
      isNavigable={isNavigable}
      isNested={isNested}
      accessLevel={meta.accessLevel}
      stuck={meta.stuck}
      as={shouldUseLink ? as : undefined}
      to={shouldUseLink ? to : undefined}
      tabIndex={isNonLeadingWithLink ? -1 : undefined}
      // add the prop conditionally like this so react devtools doesn't complain
      {...(shouldUseLink ? { hotkeyTos } : {})}
    >
      {sizeProps.style.overflow === 'auto' || sizeProps.style.overflow === 'scroll' ? (
        <ShadowOnScrollOverflowContents>{content}</ShadowOnScrollOverflowContents>
      ) : (
        content
      )}
    </TableCell>
  );
}

const AutoTableCell = memo(AutoTableCellImpl) as typeof AutoTableCellImpl;

interface AutoTableRowProps<D extends any> {
  table: ReactTable<D>;
  row: Row<D>;
  getLinkTo?: GetLinkTo<D>;
  getDrawerLinkTo?: GetLinkTo<D>;
  getEditLinkTo?: GetLinkTo<D>;
  isRowDisabled?: IsRowDisabled<D>;
  isRowSelected?: IsRowSelected<D>;
  isRowCollapsed?: IsRowCollapsed<Row<D>>;
  onToggleRowCollapse: (row: Row<D>) => void;
  onRowClick?: OnRowClick<D>;
  isMultiSelected?: boolean;
  isNested?: boolean;
  renderSubTable?: (props: SubAutoTableProps<D>) => JSX.Element;
  applyLinkToAllCells?: boolean;
  'data-index'?: number;
  dragToReorder?: boolean;
  onRowReorder?: OnRowReorder;
  enableMultiRowSelection?: boolean | ((row: Row<D>) => boolean) | AccessLevelProp | null;
}

const cellDragWidth = {
  '@notDesktop': '$60',
  '@desktop': '$44',
};

const AutoTableCellDragHandle = styled(UtilityControl, {
  opacity: 0.5,

  [selectors.hover]: {
    opacity: 1,
  },

  [selectors.focus]: {
    opacity: 1,
  },
});

const AutoTableHeadCellDrag = styled(TableHeadCell, {
  '@notDesktop': {
    widthAll: cellDragWidth['@notDesktop'],
  },

  '@desktop': {
    widthAll: cellDragWidth['@desktop'],
  },
});

const AutoTableCellDrag = styled(TableCell, {
  '@notDesktop': {
    widthAll: cellDragWidth['@notDesktop'],
  },

  '@desktop': {
    widthAll: cellDragWidth['@desktop'],
  },
});

function AutoTableRowImpl<D extends any>(
  {
    table,
    row,
    getLinkTo,
    getDrawerLinkTo,
    getEditLinkTo,
    isRowDisabled,
    isRowSelected,
    isRowCollapsed,
    onRowClick,
    onToggleRowCollapse,
    isMultiSelected,
    isNested,
    renderSubTable,
    applyLinkToAllCells,
    'data-index': dataIndex,
    dragToReorder,
    onRowReorder,
    enableMultiRowSelection,
    ...restProps
  }: AutoTableRowProps<D>,
  ref?: ForwardedRef<HTMLTableRowElement>,
) {
  const to = getLinkTo?.(row.original);
  const editTo = getEditLinkTo?.(row.original);
  const drawerTo = getDrawerLinkTo?.(row.original);
  const hotkeyTos: Record<string, To> = {};
  if (editTo) {
    hotkeyTos.e = editTo;
  }
  if (drawerTo) {
    hotkeyTos.d = drawerTo;
  }
  const isDisabled = isRowDisabled?.(row.original) ?? false;
  const isSelected = isRowSelected?.(row.original) ?? false;
  const localCollapsed = isRowCollapsed?.(row) ?? false;

  // Determine if this specific row should be selectable for multi-row selection
  const isRowSelectable = useMemo(() => {
    if (typeof enableMultiRowSelection === 'function') {
      return enableMultiRowSelection(row);
    }
    return !!enableMultiRowSelection;
  }, [enableMultiRowSelection, row]);

  const handleClick = useCallback(() => {
    onRowClick?.(row.original);
  }, [onRowClick, row.original]);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: !dragToReorder,
  });

  const onDrag = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  }, []);

  const AutoTableRowDragHandle = useCallback(() => {
    if (!dragToReorder) return null;

    return (
      <AutoTableCellDrag>
        <AutoTableCellDragHandle
          {...attributes}
          {...listeners}
          onMouseDown={handleMouseDown}
          icon="slide-vertical"
          label="Reorder"
          variant={isMultiSelected ? 'brand' : 'neutral'}
        />
      </AutoTableCellDrag>
    );
  }, [dragToReorder, attributes, listeners, isMultiSelected, handleMouseDown]);

  const subRowData = row.original;
  const subTable = renderSubTable ? renderSubTable({ data: subRowData, isNested: true }) : null;

  const combinedRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      setNodeRef(node);
      if (ref) {
        if (typeof ref === 'function') {
          ref(node);
        } else {
          // eslint-disable-next-line no-param-reassign
          ref.current = node;
        }
      }
    },
    [setNodeRef, ref],
  );

  return (
    <>
      <TableRow
        ref={combinedRef}
        key={row.id}
        isDisabled={isDisabled}
        isDragging={isDragging}
        isSelected={!!(isSelected || isMultiSelected)}
        isNested={isNested}
        onClick={handleClick}
        data-index={dataIndex}
        style={onDrag}
      >
        <TableCellBuffer side="leading" />
        <TableCellState side="leading" />
        {renderSubTable && (
          <TableCell
            collapsable
            onClick={() => onToggleRowCollapse(row)}
            collapsed={localCollapsed}
          />
        )}
        {enableMultiRowSelection && (
          <AutoTableCellSelector>
            <AutoTableRowSelector row={row} table={table} isRowSelectable={isRowSelectable} />
          </AutoTableCellSelector>
        )}
        {dragToReorder && <AutoTableRowDragHandle />}
        {row.getVisibleCells().map((cell) => (
          <AutoTableCell
            as={to ? EnhancedReactRouterLink : undefined}
            to={to ?? undefined}
            hotkeyTos={to ? hotkeyTos : undefined}
            key={cell.id}
            table={table}
            cell={cell}
            isMultiSelected={isMultiSelected}
            isNested={isNested}
            applyLinkToAllCells={applyLinkToAllCells}
          />
        ))}
        <TableCellState side="trailing" />
        <TableCellBuffer side="trailing" />
      </TableRow>
      {renderSubTable && !localCollapsed && (
        <TableRow {...restProps}>
          <TableCell hasNested colSpan={1000}>
            {subTable}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export const AutoTableRow = React.forwardRef(AutoTableRowImpl) as <D extends any>(
  props: AutoTableRowProps<D> & { ref?: ForwardedRef<HTMLTableRowElement> },
) => ReturnType<typeof AutoTableRowImpl>;

interface AutoTableBodyProps<D extends any> {
  table: ReactTable<D>;
  rows: Row<D>[];
  multiSelectedRows?: RowSelectionState;
  actions?: React.ReactNode;
  getLinkTo?: GetLinkTo<D>;
  getDrawerLinkTo?: GetLinkTo<D>;
  getEditLinkTo?: GetLinkTo<D>;
  isRowDisabled?: IsRowDisabled<D>;
  isRowSelected?: IsRowSelected<D>;
  isRowCollapsed?: IsRowCollapsed<Row<D>>;
  onRowClick?: OnRowClick<D>;
  onToggleRowCollapse: (row: Row<D>) => void;
  isNested?: boolean;
  renderSubTable?: (props: SubAutoTableProps<D>) => JSX.Element;
  applyLinkToAllCells?: boolean;
  dragToReorder?: boolean;
  onRowReorder?: OnRowReorder;
  enableMultiRowSelection?: boolean | ((row: Row<D>) => boolean) | AccessLevelProp | null;
}

type VirtualAutoTableBodyProps<D extends any> = {
  tableContainerRef: React.RefObject<HTMLDivElement | null>;
} & AutoTableBodyProps<D>;

function VirtualAutoTableBody<D extends any>({
  table,
  isRowDisabled,
  isRowSelected,
  isRowCollapsed,
  onToggleRowCollapse,
  isNested,
  getLinkTo,
  getDrawerLinkTo,
  getEditLinkTo,
  onRowClick,
  multiSelectedRows,
  renderSubTable,
  rows,
  actions,
  tableContainerRef,
  applyLinkToAllCells,
  dragToReorder,
  onRowReorder,
  enableMultiRowSelection,
}: VirtualAutoTableBodyProps<D>) {
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 36, // based on a precalculated size
    getScrollElement: () => {
      if (typeof tableContainerRef === 'object') {
        return tableContainerRef?.current ?? null;
      }
      return null;
    },
    overscan: 10,
    measureElement: (element) => element?.getBoundingClientRect().height ?? 36,
  });

  // This is a hack which solves the problem of not having the tableContainerRef.current defined.
  // This tends to happen on first and only render as a consequence of our Suspense setup
  // and will subsequently never be be defined unless there is a state change.
  // Results in an AutoTable with no rows when AutoTable is first rendered.
  // This should only be called exactly once.
  useLayoutEffect(() => {
    rowVirtualizer?.measure?.();
  }, [rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0;

  const tableBodyContent = (
    <>
      {paddingTop > 0 && (
        <tr aria-hidden>
          <td style={{ height: `${paddingTop}px` }} />
        </tr>
      )}
      {virtualRows.map((virtualRow) => {
        const row = rows[virtualRow.index];
        return (
          <AutoTableRow
            key={row.id}
            data-index={virtualRow.index}
            ref={(node) => rowVirtualizer.measureElement(node)}
            row={row}
            table={table}
            isRowDisabled={isRowDisabled}
            isRowSelected={isRowSelected}
            isRowCollapsed={isRowCollapsed}
            onToggleRowCollapse={onToggleRowCollapse}
            isNested={isNested}
            getLinkTo={getLinkTo}
            getDrawerLinkTo={getDrawerLinkTo}
            getEditLinkTo={getEditLinkTo}
            onRowClick={onRowClick}
            isMultiSelected={multiSelectedRows?.[row.id]}
            renderSubTable={renderSubTable}
            applyLinkToAllCells={applyLinkToAllCells}
            dragToReorder={dragToReorder}
            onRowReorder={onRowReorder}
            enableMultiRowSelection={enableMultiRowSelection}
          />
        );
      })}
      {actions && (
        <TableRow>
          <TableCellBuffer side="leading" />
          <TableCellState side="leading" />
          <TableCell colSpan={1000}>{actions}</TableCell>
          <TableCellState side="trailing" />
          <TableCellBuffer side="trailing" />
        </TableRow>
      )}
      {paddingBottom > 0 && (
        <tr aria-hidden>
          <td style={{ height: `${paddingBottom}px` }} />
        </tr>
      )}
    </>
  );

  return <TableBody>{tableBodyContent}</TableBody>;
}

function AutoTableBody<D extends any>({
  table,
  isRowDisabled,
  isRowSelected,
  isRowCollapsed,
  isNested,
  getLinkTo,
  getDrawerLinkTo,
  getEditLinkTo,
  onRowClick,
  onToggleRowCollapse,
  multiSelectedRows,
  renderSubTable,
  rows,
  actions,
  applyLinkToAllCells,
  dragToReorder,
  onRowReorder,
  enableMultiRowSelection,
}: AutoTableBodyProps<D>) {
  const tableBodyContent = (
    <>
      {rows.map((row) => (
        <AutoTableRow
          key={row.id}
          row={row}
          table={table}
          isRowDisabled={isRowDisabled}
          isRowSelected={isRowSelected}
          isRowCollapsed={isRowCollapsed}
          onToggleRowCollapse={onToggleRowCollapse}
          isNested={isNested}
          getLinkTo={getLinkTo}
          getDrawerLinkTo={getDrawerLinkTo}
          getEditLinkTo={getEditLinkTo}
          onRowClick={onRowClick}
          isMultiSelected={multiSelectedRows?.[row.id]}
          renderSubTable={renderSubTable}
          applyLinkToAllCells={applyLinkToAllCells}
          dragToReorder={dragToReorder}
          onRowReorder={onRowReorder}
          enableMultiRowSelection={enableMultiRowSelection}
        />
      ))}
      {actions && (
        <TableRow>
          <TableCellBuffer side="leading" />
          <TableCellState side="leading" />
          <TableCell colSpan={1000}>{actions}</TableCell>
          <TableCellState side="trailing" />
          <TableCellBuffer side="trailing" />
        </TableRow>
      )}
    </>
  );

  return <TableBody>{tableBodyContent}</TableBody>;
}

// react-table throws errors when getting row model if the sort/filter
// state is invalid. This is probably just because of the global URL-based
// search state becoming outdated, so we try resetting the filter and sort
// state and getting the rows again once.
function useRows<D>(table: ReactTable<D>) {
  try {
    return table.getRowModel()?.rows ?? [];
  } catch (err) {
    table.resetSorting();
    table.resetGlobalFilter();
    table.resetColumnFilters();
    return table.getRowModel()?.rows ?? [];
  }
}

function useCoreRows<D>(table: ReactTable<D>) {
  try {
    return table.getCoreRowModel()?.rows ?? [];
  } catch (err) {
    table.resetSorting();
    table.resetGlobalFilter();
    table.resetColumnFilters();
    return table.getCoreRowModel()?.rows ?? [];
  }
}

type CommonAutoTableProps<D> = {
  actions?: React.ReactNode;
  globalFilter?: string;
  globalFilterFn?: FilterFnOption<D>;
  sortingState?: SortingState;
  onChangeSortingState?: Dispatch<SetStateAction<SortingState>>;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  data: D[];
  columns: ColumnDef<D, any>[];
  columnVisibility?: VisibilityState;
  getLinkTo?: GetLinkTo<D>;
  getDrawerLinkTo?: GetLinkTo<D>;
  getEditLinkTo?: GetLinkTo<D>;
  isRowDisabled?: IsRowDisabled<D>;
  isRowSelected?: IsRowSelected<D>;
  onRowDeselect?: OnRowDeselect<D>;
  enableSorting?: boolean;
  enableRowSelection?: boolean | ((row: Row<D>) => boolean);
  enableMultiRowSelection?: boolean | ((row: Row<D>) => boolean) | AccessLevelProp | null;
  applyLinkToAllCells?: boolean;
  /**
   * Does not need to end with `.csv`, it will be added automatically.
   */
  exportToCSVFilename?: string;
  onRowClick?: OnRowClick<D>;
  multiSelectedRows?: RowSelectionState;
  onRowMultiSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowId?: (originalRow: D, index: number, parent?: Row<D>) => string;
  size?: 'auto' | 'small' | 'large';
  isNested?: boolean;
  renderSubTable?: (props: SubAutoTableProps<D>) => JSX.Element;
  subTableExpandedByDefault?: boolean;
  onRowsChange?: (rows: Row<D>[]) => void;
  emptyState?: React.ReactNode;
  variant?: TableVariantProp;
  dragToReorder?: boolean;
  onRowReorder?: OnRowReorder;
};

type AutoTableProps<D> = CommonAutoTableProps<D> & {
  ref?: ForwardedRef<AutoTableHandle<D>>;
} & (
    | { isVirtual?: false; tableContainerRef?: false }
    | {
        isVirtual: true;
        tableContainerRef: React.RefObject<HTMLDivElement | null>;
        renderSubTable?: false;
      }
  ) &
  (
    | {
        rowsOffset: number;
        rowsLimit: number;
      }
    | {
        rowsOffset?: never;
        rowsLimit?: never;
      }
  );

export interface AutoTableHandle<D = any> {
  exportToCSV: () => void;
  filteredRows: Row<D>[];
}

function defaultColumnCanGlobalFilter<D>(c: Column<D>): boolean {
  return c.columnDef.enableGlobalFilter ?? true;
}

const equalsIfSet: FilterFn<any> = (row, columnId: string, filterValue: unknown) =>
  !filterValue || row.getValue(columnId) === filterValue;

equalsIfSet.autoRemove = (val: any) => !val;

const arrIncludesIfSet: FilterFn<any> = (row, columnId: string, filterValue: unknown[]) =>
  !filterValue?.length || filterValue.includes(row.getValue(columnId));

arrIncludesIfSet.autoRemove = (val: any) => !val;

const prefixOverlapsIfSet: FilterFn<any> = (row, columnId: string, filterValue: string) => {
  const prefix = row.getValue<string>(columnId);

  if (!prefix || prefix === '0.0.0.0/0') return true;

  const filterIP = safeParseAddress4(filterValue);
  if (!filterIP) return true;

  const prefixAddr = safeParseAddress4(prefix);
  if (!prefixAddr) return true;

  return filterIP.isInSubnet(prefixAddr) || prefixAddr.isInSubnet(filterIP);
};

prefixOverlapsIfSet.autoRemove = (val: any) => !val;

export const filterFns = {
  equalsIfSet,
  arrIncludesIfSet,
  prefixOverlapsIfSet,
} as const;

const sortingCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const collator: SortingFn<any> = (rowA, rowB, columnID): number =>
  sortingCollator.compare(rowA.getValue(columnID), rowB.getValue(columnID));

export const sortingFns = {
  collator,
} as const;

export function AutoTable<D extends any>({
  actions,
  sortingState,
  globalFilter,
  globalFilterFn = defaultGlobalFilterFn,
  columnFilters,
  onColumnFiltersChange,
  columns,
  columnVisibility,
  data,
  getLinkTo,
  getDrawerLinkTo,
  getEditLinkTo,
  isRowDisabled,
  isRowSelected,
  onChangeSortingState,
  onRowClick,
  enableSorting = true,
  enableMultiRowSelection = false,
  exportToCSVFilename = 'export',
  onRowMultiSelectionChange,
  multiSelectedRows,
  getRowId,
  isNested,
  renderSubTable,
  subTableExpandedByDefault = false,
  isVirtual = undefined,
  tableContainerRef = undefined,
  onRowsChange,
  rowsOffset,
  rowsLimit,
  ref,
  emptyState,
  variant,
  applyLinkToAllCells,
  dragToReorder = false,
  onRowReorder,
}: AutoTableProps<D>) {
  const state = useMemo(() => {
    const s: Partial<TableState> = {
      globalFilter,
    };

    if (multiSelectedRows) {
      s.rowSelection = multiSelectedRows;
    }

    if (sortingState) {
      s.sorting = sortingState;
    }

    if (columnVisibility) {
      s.columnVisibility = columnVisibility;
    }

    if (columnFilters) {
      s.columnFilters = columnFilters;
    }

    return s;
  }, [globalFilter, sortingState, multiSelectedRows, columnVisibility, columnFilters]);

  // Helper function to determine if multi-row selection should be enabled
  const isMultiRowSelectionEnabled = useMemo(() => {
    if (typeof enableMultiRowSelection === 'boolean') {
      return enableMultiRowSelection;
    }
    if (typeof enableMultiRowSelection === 'function') {
      return true; // Function-based selection is enabled
    }
    // For AccessLevelProp, we'll need to check permissions
    // For now, we'll assume it's enabled if it's an access level and not null
    return !!enableMultiRowSelection;
  }, [enableMultiRowSelection]);

  const table = useReactTable({
    data,
    columns,
    filterFns,
    sortingFns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    enableGlobalFilter: true,
    getColumnCanGlobalFilter: defaultColumnCanGlobalFilter,
    globalFilterFn,
    onSortingChange: onChangeSortingState,
    enableMultiRowSelection: isMultiRowSelectionEnabled,
    onRowSelectionChange: onRowMultiSelectionChange,
    getRowId,
    state,
    onColumnFiltersChange,
  });

  // Helper function to get selectable rows and their selection state
  const getSelectableRowsInfo = useCallback(() => {
    if (!enableMultiRowSelection) return null;
    const { rows: tableRows } = table.getRowModel();
    const selectableRows =
      typeof enableMultiRowSelection === 'function'
        ? tableRows.filter((row) => enableMultiRowSelection(row))
        : tableRows;
    const allSelectableRowsSelected = selectableRows.every((row) => row.getIsSelected());
    const someSelectableRowsSelected = selectableRows.some((row) => row.getIsSelected());
    return { selectableRows, allSelectableRowsSelected, someSelectableRowsSelected };
  }, [table, enableMultiRowSelection]);

  // Computed selection state for the header checkbox
  const getHeaderSelectionState = useCallback(() => {
    const selectableInfo = getSelectableRowsInfo();

    if (selectableInfo) {
      return {
        ariaLabel: selectableInfo.allSelectableRowsSelected
          ? 'Deselect all rows'
          : 'Select all rows',
        checked: selectableInfo.allSelectableRowsSelected,
        indeterminate:
          !selectableInfo.allSelectableRowsSelected && selectableInfo.someSelectableRowsSelected,
      };
    }

    return {
      ariaLabel: table.getIsAllRowsSelected() ? 'Deselect all rows' : 'Select all rows',
      checked: table.getIsAllRowsSelected(),
      indeterminate:
        !table.getIsAllRowsSelected() &&
        (table.getIsAllPageRowsSelected() || table.getIsSomeRowsSelected()),
    };
  }, [getSelectableRowsInfo, table]);

  const headerSelectionState = getHeaderSelectionState();

  // Helper function to handle select all/deselect all action
  const handleSelectAllToggle = useCallback(() => {
    const selectableInfo = getSelectableRowsInfo();

    if (selectableInfo) {
      if (selectableInfo.allSelectableRowsSelected) {
        // Deselect all rows
        table.setRowSelection({});
      } else {
        // Select only selectable rows
        const newSelection = Object.fromEntries(
          selectableInfo.selectableRows.map((row) => [row.id, true]),
        );
        table.setRowSelection(newSelection);
      }
    } else {
      // Use default behavior for boolean enableMultiRowSelection
      const { rows: tableRows } = table.getRowModel();
      const selectableRows = tableRows.filter((row) => row.getCanMultiSelect());
      const allSelectableRowsSelected = selectableRows.every((row) => row.getIsSelected());

      if (allSelectableRowsSelected) {
        // Deselect all rows
        table.setRowSelection({});
      } else {
        // Select only selectable rows
        table.setRowSelection((old) => ({
          ...old,
          ...Object.fromEntries(selectableRows.map((row) => [row.id, true])),
        }));
      }
    }
  }, [getSelectableRowsInfo, table]);

  // Add hotkey handler for select all when multi-row selection is enabled
  useHotkeys(
    'alt+a',
    (e) => {
      if (!isMultiRowSelectionEnabled) return;
      e.preventDefault();

      // Trigger select all functionality
      handleSelectAllToggle();
    },
    {
      enableOnFormTags: true,
    },
    [isMultiRowSelectionEnabled, handleSelectAllToggle],
  );

  const csvConfig = useMemo(() => {
    const csvOptions = {
      fieldSeparator: ',',
      quoteStrings: true,
      decimalSeparator: '.',
      showLabels: true,
      showTitle: false,
      filename: exportToCSVFilename.replace(/\.csv$/, ''),
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
    };
    return mkConfig(csvOptions);
  }, [exportToCSVFilename]);

  const handleExportToCSV = useCallback(() => {
    const { rows } = table.getRowModel();
    const csvData = rows.map((row) =>
      Object.fromEntries(
        row
          .getAllCells()
          .map((cell) => [
            cell.column.columnDef.header &&
            ['string', 'number'].includes(typeof cell.column.columnDef.header)
              ? cell.column.columnDef.header
              : (cell.column.columnDef?.meta?.tooltip?.label?.contents ?? ''),
            cell.getValue(),
          ]),
      ),
    );

    const csv = generateCsv(csvConfig)(csvData);
    download(csvConfig)(csv);
  }, [csvConfig, table]);

  const filteredRows = useRows(table);
  const coreRows = useCoreRows(table);

  const { setColumnVisibility } = table;

  const columnVisibilityEffect = useCallback(() => {
    if (!columnVisibility && coreRows.length) {
      const visibility: VisibilityState = {};
      const hideIfAllNullishColumns = columns.filter((col) => col.meta?.hideIfAllNullish);
      if (hideIfAllNullishColumns.length > 0) {
        Object.assign(
          visibility,
          Object.fromEntries(
            hideIfAllNullishColumns
              .filter(
                (col): col is typeof col & { id: NonNullable<(typeof col)['id']> } => !!col.id,
              )
              .map((col) => [col.id, coreRows.some((row) => row.getValue(col.id) != null)]),
          ),
        );
      }

      const hideIfAllEqualColumns = columns.filter((col) => col.meta?.hideIfAllEqual);
      if (hideIfAllEqualColumns.length > 0) {
        Object.assign(
          visibility,
          Object.fromEntries(
            hideIfAllEqualColumns
              .filter(
                (col): col is typeof col & { id: NonNullable<(typeof col)['id']> } => !!col.id,
              )
              .map((col) => {
                const firstVal = coreRows[0]?.getValue(col.id);
                return [col.id, coreRows.some((row) => row.getValue(col.id) !== firstVal)];
              }),
          ),
        );
      }

      if (Object.keys(visibility).length > 0) {
        setColumnVisibility(visibility);
      }
    }
  }, [columnVisibility, coreRows, columns, setColumnVisibility]);

  useEffect(() => {
    columnVisibilityEffect();
  }, [columnVisibilityEffect]);

  useImperativeHandle(
    ref,
    () => ({
      exportToCSV: handleExportToCSV,
      filteredRows,
    }),
    [handleExportToCSV, filteredRows],
  );

  useEffect(() => {
    onRowsChange?.(filteredRows);
  }, [filteredRows, onRowsChange]);

  const rows = useMemo(
    () =>
      rowsOffset != null && rowsLimit != null
        ? filteredRows.slice(rowsOffset, rowsLimit ? (rowsOffset ?? 0) + rowsLimit : undefined)
        : filteredRows,
    [filteredRows, rowsOffset, rowsLimit],
  );

  const hasSubTable = renderSubTable;
  const [collapsed, setCollapsed] = useState(subTableExpandedByDefault);
  const [expandedRowsSet, setExpandedRowsSet] = useState(() =>
    subTableExpandedByDefault ? new Set(rows.map((row) => row.id)) : new Set(),
  );

  useEffect(() => {
    if (subTableExpandedByDefault) {
      setExpandedRowsSet(() => new Set(rows.map((row) => row.id)));
    }
  }, [subTableExpandedByDefault, rows]);

  const onToggleRowCollapse = useCallback(
    (row: Row<D>) => {
      const newExpandedSet = new Set([...expandedRowsSet]);
      if (expandedRowsSet.has(row.id)) {
        newExpandedSet.delete(row.id);
      } else {
        newExpandedSet.add(row.id);
      }
      setExpandedRowsSet(newExpandedSet);
    },
    [expandedRowsSet],
  );
  const isRowCollapsed = useCallback(
    (row: Row<D>) => !expandedRowsSet.has(row.id),
    [expandedRowsSet],
  );

  // DnD hooks - must be called at the top level
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));
  const rowIds = rows.map((row) => row.id);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!onRowReorder || !over || active.id === over.id) {
        return;
      }

      const activeIndex = rows.findIndex((row) => row.id === active.id);
      const overIndex = rows.findIndex((row) => row.id === over.id);

      if (activeIndex !== -1 && overIndex !== -1) {
        onRowReorder(activeIndex, overIndex);
      }
    },
    [onRowReorder, rows],
  );

  if (emptyState && rows.length === 0) {
    return emptyState;
  }

  const tableContent = (
    <Table isNested={isNested} multiRowSelection={!!isMultiRowSelectionEnabled} variant={variant}>
      <TableHead>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableHeadRow key={headerGroup.id}>
            <TableCellBuffer side="leading" head />
            <TableCellState side="leading" head />
            {hasSubTable && (
              <TableHeadCell
                collapsable
                onClick={() => {
                  setCollapsed(!collapsed);
                  if (collapsed) {
                    setExpandedRowsSet(new Set());
                  } else {
                    setExpandedRowsSet(new Set([...rows.map((row) => row.id)]));
                  }
                }}
                collapsed={collapsed}
              />
            )}
            {isMultiRowSelectionEnabled && (
              <AutoTableHeadCellSelector key={SELECT_COLUMN_ID} hideSortIcon>
                <AutoTableRowSelectorContainer>
                  <CheckboxInput
                    aria-label={headerSelectionState.ariaLabel}
                    checked={headerSelectionState.checked}
                    indeterminate={headerSelectionState.indeterminate}
                    disabled={false}
                    onClick={(e: React.MouseEvent<HTMLInputElement>): void => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectAllToggle();
                    }}
                  />
                </AutoTableRowSelectorContainer>
              </AutoTableHeadCellSelector>
            )}
            {dragToReorder && <AutoTableHeadCellDrag hideSortIcon />}
            {headerGroup.headers.map((header) => {
              // Skip the multi-select cell since we render it separately
              if (header.column.id === SELECT_COLUMN_ID) {
                return null;
              }
              return (
                <TableHeadCell
                  key={header.id}
                  sortDirection={header.column.getIsSorted()}
                  onClick={header.column.getToggleSortingHandler()}
                  {...getSizeProps(table, header.column)}
                  accessLevel={header.column.columnDef.meta?.accessLevel}
                  isLeading={header.column.columnDef.meta?.isLeading}
                  stuck={header.column.columnDef.meta?.stuck}
                  tooltip={header.column.columnDef.meta?.tooltip}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHeadCell>
              );
            })}
            <TableCellState side="trailing" head />
            <TableCellBuffer side="trailing" head />
          </TableHeadRow>
        ))}
      </TableHead>
      {isVirtual && tableContainerRef ? (
        <VirtualAutoTableBody
          table={table}
          rows={rows}
          tableContainerRef={tableContainerRef}
          isRowDisabled={isRowDisabled}
          isRowSelected={isRowSelected}
          isRowCollapsed={isRowCollapsed}
          onToggleRowCollapse={onToggleRowCollapse}
          isNested={isNested}
          getLinkTo={getLinkTo}
          getDrawerLinkTo={getDrawerLinkTo}
          getEditLinkTo={getEditLinkTo}
          onRowClick={onRowClick}
          multiSelectedRows={multiSelectedRows}
          renderSubTable={renderSubTable}
          actions={actions}
          applyLinkToAllCells={applyLinkToAllCells}
          dragToReorder={dragToReorder}
          onRowReorder={onRowReorder}
          enableMultiRowSelection={enableMultiRowSelection}
        />
      ) : (
        <AutoTableBody
          table={table}
          rows={rows}
          isRowDisabled={isRowDisabled}
          isRowSelected={isRowSelected}
          isRowCollapsed={isRowCollapsed}
          onToggleRowCollapse={onToggleRowCollapse}
          isNested={isNested}
          getLinkTo={getLinkTo}
          getDrawerLinkTo={getDrawerLinkTo}
          getEditLinkTo={getEditLinkTo}
          onRowClick={onRowClick}
          multiSelectedRows={multiSelectedRows}
          renderSubTable={renderSubTable}
          actions={actions}
          applyLinkToAllCells={applyLinkToAllCells}
          dragToReorder={dragToReorder}
          onRowReorder={onRowReorder}
          enableMultiRowSelection={enableMultiRowSelection}
        />
      )}
    </Table>
  );

  if (dragToReorder) {
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
          {tableContent}
        </SortableContext>
      </DndContext>
    );
  }

  return tableContent;
}
